import React from 'react';
import { useNavigate } from 'react-router-dom';
import useHttp from '../../hooks/http.hook';
import { useBranchNav } from '../../hooks/branchNav.hook';
import '../business/Business.css';
import './PromotionsPage.css';
import CreateBusinessHeader from '../business/components/CreateBusinessHeader';
import PromotionActiveToggle from './components/PromotionActiveToggle';

function PromotionsPage() {
  const navigate = useNavigate();
  const { branchHomePath } = useBranchNav();
  const { requestWithMeta } = useHttp();

  const [promotions, setPromotions] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState(null);
  const [togglingId, setTogglingId] = React.useState(null);

  React.useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);

    (async () => {
      try {
        const res = await requestWithMeta('/operator/promotions', 'GET');
        if (cancelled) return;
        if (!res?.ok) {
          setError('Не удалось загрузить акции');
          setPromotions([]);
          return;
        }
        const list = Array.isArray(res?.data) ? res.data : [];
        setPromotions(list);
      } catch (_) {
        if (!cancelled) {
          setError('Ошибка загрузки');
          setPromotions([]);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => { cancelled = true; };
  }, [requestWithMeta]);

  const handleBack = () => {
    navigate(branchHomePath);
  };

  const handleCreatePromotion = () => {
    navigate('/promotions/choose');
  };

  const togglePromotionActive = async (promotion) => {
    const id = promotion?.id ?? promotion?._id;
    if (id === undefined || id === null) return;
    if (togglingId !== null) return;

    const prevActive = promotion?.active === true;
    const nextActive = !prevActive;

    setTogglingId(String(id));
    setError(null);

    try {
      const res = await requestWithMeta(
        `/operator/promotions/${encodeURIComponent(id)}/active?active=${encodeURIComponent(String(nextActive))}`,
        'POST'
      );

      if (!res?.ok) {
        setError(res?.data?.error || res?.data?.message || 'Не удалось изменить статус акции');
        return;
      }

      // apply new state only after successful response
      setPromotions((prev) =>
        prev.map((p) => {
          const pid = p?.id ?? p?._id;
          if (String(pid) !== String(id)) return p;
          return { ...p, active: nextActive };
        })
      );
    } catch (_) {
      setError('Ошибка изменения статуса');
    } finally {
      setTogglingId(null);
    }
  };

  return (
    <div className="promotions-page-root">
      <CreateBusinessHeader
        title="Акции"
        subtitle=""
        onBack={handleBack}
      />

      <main className="promotions-page-content">
        {loading ? (
          <div className="promotions-page-loading" aria-busy="true">
            <div className="promotions-page-skeleton promotions-page-skeleton--block" />
            <div className="promotions-page-skeleton promotions-page-skeleton--block" />
            <div className="promotions-page-skeleton promotions-page-skeleton--block" />
          </div>
        ) : error ? (
          <div className="promotions-page-error">{error}</div>
        ) : promotions.length === 0 ? (
          <div className="promotions-page-empty">Нет акций</div>
        ) : (
          <ul className="promotions-page-list" aria-label="Список акций">
            {promotions.map((promotion) => {
              const id = promotion?.id ?? promotion?._id;
              const name = promotion?.name ?? `Акция #${id}`;
              const active = promotion?.active === true;
              const isToggling = togglingId !== null && String(togglingId) === String(id);

              return (
                <li
                  key={id}
                  className="promotions-page-item"
                  role="button"
                  tabIndex={0}
                  onClick={() => navigate(`/promotions/${id}/edit`)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      navigate(`/promotions/${id}/edit`);
                    }
                  }}
                >
                  <div className="promotions-page-itemRow">
                    <span className="promotions-page-itemName">{name}</span>
                    <PromotionActiveToggle
                      active={active}
                      loading={isToggling}
                      onToggle={(e) => {
                        // prevent opening edit page when toggling
                        e?.stopPropagation?.();
                        togglePromotionActive(promotion);
                      }}
                      label={active ? 'Нажмите, чтобы выключить' : 'Нажмите, чтобы включить'}
                    />
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </main>

      <footer className="promotions-page-footerRow">
        <button
          type="button"
          className="promotions-page-footerButton promotions-page-footerButtonPrimary"
          onClick={handleCreatePromotion}
        >
          Создать акцию
        </button>
      </footer>
    </div>
  );
}

export default PromotionsPage;
